import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Check, ChevronDown, Loader2, RefreshCw, Save, Search, ShieldCheck, Trash2, UserRound } from 'lucide-react';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components_dashboard/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components_dashboard/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components_dashboard/ui/select';
import { Switch } from '../components_dashboard/ui/switch';
import { Textarea } from '../components_dashboard/ui/textarea';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';
import { CallAnalyticsLimitsDialog } from '../features/call-analytics/admin-limits-dialog';
import {
  canDeleteInfieldLanguage,
  CREATE_INFIELD_LANGUAGE_VALUE,
  DEFAULT_INFIELD_LANGUAGE_OPTIONS,
  normalizeInfieldLanguageName,
  normalizeInfieldLanguageOptions,
} from '../lib/infieldLanguages';
import {
  DEFAULT_AUTO_DISCONNECT_MESSAGE,
  DEFAULT_AUTO_DISCONNECT_TIMEOUT_SECONDS,
  DEFAULT_SHORT_CALL_RETRY_SECONDS,
  DEFAULT_CALL_RETRY_ENABLED,
  DEFAULT_SHORT_CALL_RETRY_ENABLED,
  DEFAULT_LLM_SKIP_ENABLED,
  DEFAULT_LLM_SKIP_SECONDS,
  DEFAULT_CALL_RETRY_ATTEMPTS,
  DEFAULT_CALL_RETRY_DELAY_MINUTES,
  DEFAULT_USER_AWAY_TIMEOUT_SECONDS,
  DEFAULT_AWAY_FIRST_MESSAGE,
  DEFAULT_AWAY_DISCONNECT_COUNTDOWN_SECONDS,
  DEFAULT_AWAY_DISCONNECT_MESSAGE,
  DEFAULT_TRANSFER_ENABLED,
  DEFAULT_TRANSFER_ON_TIME_LIMIT_ENABLED,
  DEFAULT_TRANSFER_ON_TIME_LIMIT_LABEL,
  DEFAULT_TRANSFER_ON_TIME_LIMIT_MESSAGE,
  DEFAULT_TRANSFER_AFTER_QUESTIONS_ENABLED,
  DEFAULT_TRANSFER_AFTER_QUESTIONS_COUNT,
  DEFAULT_TRANSFER_AFTER_QUESTIONS_LABEL,
  DEFAULT_TRANSFER_AFTER_QUESTIONS_MESSAGE,
  DEFAULT_NOISE_CANCELLATION_ENABLED,
  DEFAULT_SPEAKER_ISOLATION_ENABLED,
  DEFAULT_NEXGEN_THINKER_ENABLED,
  normalizeTransferDestinations,
} from '../lib/userAccess';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = ['member', 'viewer', 'admin', 'owner', 'monitor_langfuse', 'monitor_timeliner', 'monitor_all'];

const ADMIN_SETTINGS_TABS = [
  { label: 'Feature Flags', value: 'feature-flags' },
  { label: 'Limit', value: 'limits' },
  { label: 'Credit Alert', value: 'credit-alert' },
  { label: 'Agent Scheduling', value: 'agent-scheduling' },
  { label: 'Voice Access', value: 'voice-access' },
  { label: 'Org Voices', value: 'org-voices' },
  { label: 'DID Number', value: 'did-number-access' },
];

const BOOLEAN_FIELDS = [
  { key: 'agent_create_feature', label: 'Agent Create Feature', description: 'Allow agent creation, publishing, editing, rename, and delete.' },
  { key: 'external_prompt_feature', label: 'External Prompt', description: 'Show editable business sections while calls use the production prompt. Active only when Agent Create is on.' },
  { key: 'analytic', label: 'Analytics', description: 'Allow access to the analytics page and export endpoints.' },
  { key: 'workflow_feature', label: 'Workflow', description: 'Show the Workflows tab: build and run multi-step call workflows. Off by default.' },
  { key: 'crm_feature', label: 'CRM', description: 'Show the CRM tab: lead categories, leads, manual calling, and workflow batch upload. Off by default.' },
  { key: 'chatbot_feature', label: 'Chatbot', description: 'Show the Chatbot tab. Off by default.' },
  { key: 'call_analytics_feature', label: 'Call Analytics', description: 'Per-call analytics (acoustic, emotion, sentiment, QA, compliance, CX). Paid per analysed call. Off by default.' },
  { key: 'whatsapp_feature', label: 'WhatsApp', description: 'Show the Settings > WhatsApp tab (connect and manage numbers). Off by default.' },
  { key: 'products_feature', label: 'Products', description: 'Show the Products page, where this organisation can view the products TechLadder offers (admins manage them under Admin > Products). Off by default.' },
  { key: 'knowledge_feature', label: 'Knowledge Bases', description: 'Show the Knowledge page and the agent Knowledge panel: upload documents agents can search during calls. Each agent also needs its own toggle on. Off by default.' },
  { key: 'add_credit_feature', label: 'Add Credit Button', description: 'Show the Add credit button next to the credit balance. Products access is also required. Off by default.' },
  { key: 'voice_clone_feature', label: 'Voice Cloning', description: 'Let this organisation clone voices from a recording. Each clone is billed by the TTS provider, so this is off by default.' },
  { key: 'timeliner', label: 'Timeliner', description: 'Allow access to the timeliner monitoring dashboard.' },
  { key: 'demo_mode', label: 'Demo Mode', description: 'Fill the supervision console with realistic sample live calls and rota data for sales demos. Off by default.' },
  { key: 'call_outcome', label: 'Call Outcome', description: 'Show call outcome actions and outcome data in analytics.' },
  { key: 'conversation_metadata', label: 'Conversation Metadata', description: 'Show recordings, transcript, and conversation details in analytics.' },
  { key: 'show_customer_phone', label: 'Show Customer Phone', description: 'Show customer phone numbers from call summaries in analytics.' },
  { key: 'background_noise_simulation', label: 'Background Noise', description: 'Enable background noise simulation in scheduled calls.' },
  { key: 'interruption_handling', label: 'Interruption Handling', description: 'Allow callers to interrupt the agent while speaking.' },
  { key: 'primary_did_rotation', label: 'Primary DID Rotation', description: 'Randomly choose from allowed primary DID numbers on first-time calls. When off, use the selected DID number. Cannot be on with DID Limit Rotation.' },
  { key: 'did_limit_rotation', label: 'DID Limit Rotation', description: 'Rotate across allowed DIDs using per-number call_limit (LRU). When on, schedule stores the full allowed DID pool. Cannot be on with Primary DID Rotation.' },
  { key: 'hang_up_on_silence', label: 'Hang Up On Silence', description: 'Store whether this account should hang up on long silence.' },
  { key: 'total_call_timeout', label: 'Total Call Timeout', description: 'Store whether total call timeout controls are enabled for this account.' },
  { key: 'is_skip_credit_enabled', label: 'Skip Credit Limit', description: 'When enabled, show the Skip Credit Limit controls for this user.' },
  { key: 'audit_and_billing', label: 'Audit & Billing', description: 'Show the Audit & Billing section to the user.' },
  { key: 'command_center', label: 'Command Your Voice Fleet', description: 'Show the Command your voice fleet / Ask Your Data sections.' },
  { key: 'supervision', label: 'Supervisor Console', description: 'Show the Supervisor tab: live call monitoring and guidance for this account.' },
  { key: 'inbound_calling_feature', label: 'Inbound Calling', description: 'Show the Phone Numbers page so this account can answer its numbers with an agent. Off by default.' },
];

const CREDIT_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'started', label: 'Started' },
];

const DEFAULT_AGENT_SCHEDULE_OVERRIDE = {
  schedule_time_start: '11:00',
  schedule_time_end: '20:00',
  schedule_timezone: 'Asia/Kolkata (IST, UTC+5:30)',
  callback_time_start: '11:00',
  callback_time_end: '20:00',
  short_call_retry_seconds: DEFAULT_SHORT_CALL_RETRY_SECONDS,
  auto_disconnect_timeout_seconds: DEFAULT_AUTO_DISCONNECT_TIMEOUT_SECONDS,
  auto_disconnect_message: DEFAULT_AUTO_DISCONNECT_MESSAGE,
  user_away_timeout_seconds: DEFAULT_USER_AWAY_TIMEOUT_SECONDS,
  away_first_message: DEFAULT_AWAY_FIRST_MESSAGE,
  away_disconnect_countdown_seconds: DEFAULT_AWAY_DISCONNECT_COUNTDOWN_SECONDS,
  away_disconnect_message: DEFAULT_AWAY_DISCONNECT_MESSAGE,
  call_retry_enabled: DEFAULT_CALL_RETRY_ENABLED,
  short_call_retry_enabled: DEFAULT_SHORT_CALL_RETRY_ENABLED,
  llm_skip_enabled: DEFAULT_LLM_SKIP_ENABLED,
  llm_skip_seconds: DEFAULT_LLM_SKIP_SECONDS,
  call_retry_attempts: DEFAULT_CALL_RETRY_ATTEMPTS,
  call_retry_delay_minutes: DEFAULT_CALL_RETRY_DELAY_MINUTES,
  transfer_enabled: DEFAULT_TRANSFER_ENABLED,
  transfer_destinations: {},
  transfer_on_time_limit_enabled: DEFAULT_TRANSFER_ON_TIME_LIMIT_ENABLED,
  transfer_on_time_limit_label: DEFAULT_TRANSFER_ON_TIME_LIMIT_LABEL,
  transfer_on_time_limit_message: DEFAULT_TRANSFER_ON_TIME_LIMIT_MESSAGE,
  transfer_after_questions_enabled: DEFAULT_TRANSFER_AFTER_QUESTIONS_ENABLED,
  transfer_after_questions_count: DEFAULT_TRANSFER_AFTER_QUESTIONS_COUNT,
  transfer_after_questions_label: DEFAULT_TRANSFER_AFTER_QUESTIONS_LABEL,
  transfer_after_questions_message: DEFAULT_TRANSFER_AFTER_QUESTIONS_MESSAGE,
  noise_cancellation: DEFAULT_NOISE_CANCELLATION_ENABLED,
  speaker_isolation: DEFAULT_SPEAKER_ISOLATION_ENABLED,
  thinking_nextgen: DEFAULT_NEXGEN_THINKER_ENABLED,
};

const DEFAULT_PROMOTIONAL_CREDIT_ALERT_THRESHOLDS = [90, 70, 50];
const DEFAULT_TOP_UP_CREDIT_ALERT_THRESHOLDS = [10000, 5000, 2000, 500];
const CREATE_CREDIT_ALERT_THRESHOLD_VALUE = 'credit-alert-threshold:create';

// Expand the stored delay spec ("60" or "30,60") into one value per attempt.
// Pads with the last value when shorter than `attempts`, truncates when longer.
function getPerAttemptDelays(delayValue, attempts, fallback = DEFAULT_CALL_RETRY_DELAY_MINUTES) {
  const count = Math.max(0, parseInt(attempts, 10) || 0);
  const split = (value) => String(value ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
  let base = split(delayValue);
  if (!base.length) base = split(fallback);
  if (!base.length) base = ['60'];
  const result = [];
  for (let i = 0; i < count; i += 1) {
    // Beyond the provided values, repeat the last one (so e.g. "30,60" with
    // 3 attempts -> 30, 60, 60).
    result.push(i < base.length ? base[i] : base[base.length - 1]);
  }
  return result;
}

// Serialize/parse the label→number transfer map as "label: number" lines.
function transferMapToText(map) {
  if (!map || typeof map !== 'object') return '';
  return Object.entries(map)
    .map(([label, number]) => `${label}: ${number}`)
    .join('\n');
}

function parseTransferMapText(text) {
  const map = {};
  String(text || '')
    .split('\n')
    .forEach((line) => {
      const idx = line.indexOf(':');
      if (idx < 0) return;
      const label = line.slice(0, idx).trim();
      const number = line.slice(idx + 1).trim();
      if (label && number) map[label] = number;
    });
  return map;
}

function normalizeCreditAlertThresholds(thresholds, fallbackValues) {
  const source = Array.isArray(thresholds) ? thresholds : fallbackValues;
  const normalized = source
    .map((item) => Number(item ?? 0))
    .filter((item) => Number.isFinite(item) && item > 0)
    .sort((a, b) => b - a);
  return normalized.length ? Array.from(new Set(normalized)) : fallbackValues;
}

// Free-text editor for the label→number map. Keeps its own text state so typing
// a partial line ("doctor: ") isn't fought by re-serializing the parsed map; the
// parsed map flows up via onChange for saving. Backend normalizes on save.
function TransferDestinationsEditor({ value, onChange }) {
  const [text, setText] = useState(() => transferMapToText(value));
  return (
    <Textarea
      rows={3}
      className="font-mono text-xs"
      placeholder={'human_agent: +911234567890\ndoctor: +919999900000'}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseTransferMapText(e.target.value));
      }}
    />
  );
}

export default function AdminDashboard() {
  const { token, user, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingUser, setLoadingUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allVoices, setAllVoices] = useState([]);
  // The organisation the selected user owns, and the catalogue voices granted to it.
  const [orgForUser, setOrgForUser] = useState(null);
  const [orgVoicesDraft, setOrgVoicesDraft] = useState(null);
  const [orgVoicesSaving, setOrgVoicesSaving] = useState(false);
  const [allDidNumbers, setAllDidNumbers] = useState([]);
  const [userAgents, setUserAgents] = useState([]);
  const [userWorkflows, setUserWorkflows] = useState([]);
  const [agentScheduleSearchQuery, setAgentScheduleSearchQuery] = useState('');
  const [activeSettingsTab, setActiveSettingsTab] = useState('feature-flags');
  const [callAnalyticsLimitsOpen, setCallAnalyticsLimitsOpen] = useState(false);
  const [infieldLanguageSelectValue, setInfieldLanguageSelectValue] = useState('Hindi');
  const [createInfieldLanguageOpen, setCreateInfieldLanguageOpen] = useState(false);
  const [deleteInfieldLanguageTarget, setDeleteInfieldLanguageTarget] = useState('');
  const [draftInfieldLanguageName, setDraftInfieldLanguageName] = useState('');
  const [createCreditAlertThresholdOpen, setCreateCreditAlertThresholdOpen] = useState(false);
  const [draftCreditAlertThresholdAmount, setDraftCreditAlertThresholdAmount] = useState('');
  const [creditAlertThresholdOpenGroup, setCreditAlertThresholdOpenGroup] = useState(null);
  const [creditAlertThresholdTargetGroup, setCreditAlertThresholdTargetGroup] = useState('top_up');

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;
    return users.filter((entry) =>
      String(entry.name || '').toLowerCase().includes(query)
      || String(entry.email || '').toLowerCase().includes(query)
      || String(entry.role || '').toLowerCase().includes(query)
    );
  }, [searchQuery, users]);

  const selectedRetryDidNumbers = useMemo(() => {
    if (!draft) return [];
    const selectedIds = new Set(draft.retry_did_numbers || []);
    return allDidNumbers.filter((didNumber) => selectedIds.has(didNumber.id));
  }, [allDidNumbers, draft]);

  const infieldLanguageOptions = useMemo(
    () => normalizeInfieldLanguageOptions(draft?.infield_languages),
    [draft?.infield_languages]
  );

  const activeVoiceNames = useMemo(
    () => new Set(allVoices.map((voice) => voice.local_voice_name).filter(Boolean)),
    [allVoices]
  );

  const activeDidNumberIds = useMemo(
    () => new Set(allDidNumbers.map((didNumber) => didNumber.id).filter(Boolean)),
    [allDidNumbers]
  );

  const filteredAgentScheduleRows = useMemo(() => {
    const rows = [
      ...userAgents.map((agent) => ({
        key: agent.agent_id,
        overrideKey: agent.agent_id,
        visibilityKey: agent.agent_id,
        label: agent.agent_name || agent.agent_id,
        isWorkflow: false,
      })),
      ...userWorkflows.map((wf) => ({
        key: `workflow:${wf.id}`,
        overrideKey: `workflow:${wf.id}`,
        visibilityKey: `workflow:${wf.id}`,
        label: wf.agent_name || wf.client_name || wf.id,
        isWorkflow: true,
      })),
    ];

    const query = agentScheduleSearchQuery.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      String(row.label || '').toLowerCase().includes(query)
      || String(row.overrideKey || '').toLowerCase().includes(query)
      || String(row.visibilityKey || '').toLowerCase().includes(query)
    );
  }, [agentScheduleSearchQuery, userAgents, userWorkflows]);

  useEffect(() => {
    setInfieldLanguageSelectValue((current) => (
      infieldLanguageOptions.includes(current)
        ? current
        : (infieldLanguageOptions[0] || DEFAULT_INFIELD_LANGUAGE_OPTIONS[0])
    ));
  }, [infieldLanguageOptions]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(response.data?.users) ? response.data.users : [];
      setUsers(rows);
      setSelectedUserId((current) => {
        if (current && rows.some((entry) => entry.id === current)) return current;
        return rows[0]?.id || '';
      });
    } catch (error) {
      console.error('Failed to fetch admin users', error);
      toast.error(error.response?.data?.detail || 'Failed to load users.');
    } finally {
      setLoadingUsers(false);
    }
  }, [token]);

  const fetchUserDetails = useCallback(async (userId) => {
    if (!token || !userId) return;
    try {
      setLoadingUser(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedUser(response.data);
      setDraft(response.data);
    } catch (error) {
      console.error('Failed to fetch user details', error);
      toast.error(error.response?.data?.detail || 'Failed to load user details.');
    } finally {
      setLoadingUser(false);
    }
  }, [token]);

  // The organisation grant is one level above the per-user one: TechLadder decides what
  // the organisation may use, its owner then decides which member gets which voice.
  const fetchOrgVoices = useCallback(async (userId) => {
    if (!token || !userId) return;
    setOrgForUser(null);
    setOrgVoicesDraft(null);
    try {
      const response = await axios.get(`${apiUrl}/api/v2/admin/users/${userId}/organization`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrgForUser(response.data || null);
      // null means never restricted, which is not the same as granting nothing.
      setOrgVoicesDraft(response.data?.allowed_voices ?? null);
    } catch (error) {
      console.error('Failed to fetch organisation voices', error);
    }
  }, [token]);

  const saveOrgVoices = useCallback(async (allowedVoices) => {
    if (!token || !orgForUser?.id) return;
    try {
      setOrgVoicesSaving(true);
      const response = await axios.patch(
        `${apiUrl}/api/v2/admin/organizations/${orgForUser.id}/voices`,
        { allowed_voices: allowedVoices },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrgForUser(response.data || orgForUser);
      setOrgVoicesDraft(response.data?.allowed_voices ?? allowedVoices);
      toast.success('Organisation voices updated.');
    } catch (error) {
      console.error('Failed to save organisation voices', error);
      toast.error(error.response?.data?.detail || 'Failed to update organisation voices.');
    } finally {
      setOrgVoicesSaving(false);
    }
  }, [token, orgForUser]);

  const fetchUserAgents = useCallback(async (userId) => {
    if (!token || !userId) return;
    try {
      const response = await axios.get(`${apiUrl}/api/v1/admin/users/${userId}/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserAgents(response.data?.agents || []);
    } catch (error) {
      console.error('Failed to fetch user agents', error);
      setUserAgents([]);
    }
  }, [token]);

  const fetchUserWorkflows = useCallback(async (userId) => {
    if (!token || !userId) return;
    try {
      const response = await axios.get(`${apiUrl}/api/v1/admin/users/${userId}/multi-agent-prompts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserWorkflows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch user workflows', error);
      setUserWorkflows([]);
    }
  }, [token]);

  const fetchAllVoices = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${apiUrl}/api/v1/admin/voices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const activeVoices = (response.data?.voices || []).filter(v => v.active);
      setAllVoices(activeVoices);
    } catch (error) {
      console.error('Failed to fetch admin voices', error);
    }
  }, [token]);

  const fetchAllDidNumbers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${apiUrl}/api/v1/admin/did-numbers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const activeDidNumbers = (response.data?.did_numbers || []).filter(
        (didNumber) => String(didNumber?.status || '').toLowerCase() === 'active'
      );
      setAllDidNumbers(activeDidNumbers);
    } catch (error) {
      console.error('Failed to fetch admin DID numbers', error);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
    fetchAllVoices();
    fetchAllDidNumbers();
  }, [fetchUsers, fetchAllVoices, fetchAllDidNumbers]);

  useEffect(() => {
    if (selectedUserId) {
      fetchUserDetails(selectedUserId);
      fetchUserAgents(selectedUserId);
      fetchUserWorkflows(selectedUserId);
      fetchOrgVoices(selectedUserId);
    } else {
      setSelectedUser(null);
      setDraft(null);
      setUserAgents([]);
      setUserWorkflows([]);
      setOrgForUser(null);
      setOrgVoicesDraft(null);
    }
  }, [fetchUserDetails, fetchUserAgents, fetchUserWorkflows, fetchOrgVoices, selectedUserId]);

  const handleBooleanChange = (field, checked) => {
    setDraft((current) => {
      const next = { ...current, [field]: checked };
      if (field === 'primary_did_rotation' && checked) {
        next.did_limit_rotation = false;
      }
      if (field === 'did_limit_rotation' && checked) {
        next.primary_did_rotation = false;
      }
      return next;
    });
  };

  const handleVoiceToggle = (voiceName, checked) => {
    setDraft((current) => {
      let allowed = current.allowed_voices || [];
      if (checked) {
        if (!allowed.includes(voiceName)) {
          allowed = [...allowed, voiceName];
        }
      } else {
        allowed = allowed.filter(v => v !== voiceName);
      }
      return { ...current, allowed_voices: allowed };
    });
  };

  const handleDidNumberToggle = (didNumberId, checked) => {
    setDraft((current) => {
      let allowed = current.allowed_did_numbers || [];
      if (checked) {
        if (!allowed.includes(didNumberId)) {
          allowed = [...allowed, didNumberId];
        }
      } else {
        allowed = allowed.filter((value) => value !== didNumberId);
      }
      return { ...current, allowed_did_numbers: allowed };
    });
  };

  const handleRetryDidNumberToggle = (didNumberId, checked) => {
    setDraft((current) => {
      let selected = current.retry_did_numbers || [];
      if (checked) {
        if (!selected.includes(didNumberId)) {
          selected = [...selected, didNumberId];
        }
      } else {
        selected = selected.filter((value) => value !== didNumberId);
      }
      return { ...current, retry_did_numbers: selected };
    });
  };

  const handleHiddenAgentToggle = (visibilityKey, checked) => {
    setDraft((current) => {
      const currentHidden = current.hidden_agent_ids || [];
      const hiddenSet = new Set(currentHidden);
      if (checked) {
        hiddenSet.add(visibilityKey);
      } else {
        hiddenSet.delete(visibilityKey);
      }
      return { ...current, hidden_agent_ids: Array.from(hiddenSet) };
    });
  };

  const handleFieldChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handleAddCreditAlertThreshold = () => {
    const thresholdAmount = Number(draftCreditAlertThresholdAmount || 0);
    if (!Number.isFinite(thresholdAmount) || thresholdAmount <= 0) {
      toast.error('Enter a valid threshold amount.');
      return;
    }
    setDraft((current) => {
      const key = creditAlertThresholdTargetGroup === 'promotional'
        ? 'credit_alert_promotional_thresholds'
        : 'credit_alert_top_up_thresholds';
      const fallbackValues = creditAlertThresholdTargetGroup === 'promotional'
        ? DEFAULT_PROMOTIONAL_CREDIT_ALERT_THRESHOLDS
        : DEFAULT_TOP_UP_CREDIT_ALERT_THRESHOLDS;
      const currentThresholds = normalizeCreditAlertThresholds(current?.[key], fallbackValues);
      return {
        ...current,
        [key]: normalizeCreditAlertThresholds([
          ...currentThresholds,
          thresholdAmount,
        ], fallbackValues),
      };
    });
    setDraftCreditAlertThresholdAmount('');
    setCreateCreditAlertThresholdOpen(false);
  };

  const handleRemoveCreditAlertThreshold = (group, thresholdAmount) => {
    setDraft((current) => ({
      ...current,
      [group === 'promotional' ? 'credit_alert_promotional_thresholds' : 'credit_alert_top_up_thresholds']:
        normalizeCreditAlertThresholds(
          normalizeCreditAlertThresholds(
            current?.[group === 'promotional' ? 'credit_alert_promotional_thresholds' : 'credit_alert_top_up_thresholds'],
            group === 'promotional' ? DEFAULT_PROMOTIONAL_CREDIT_ALERT_THRESHOLDS : DEFAULT_TOP_UP_CREDIT_ALERT_THRESHOLDS,
          ).filter((item) => item !== thresholdAmount),
          group === 'promotional' ? DEFAULT_PROMOTIONAL_CREDIT_ALERT_THRESHOLDS : DEFAULT_TOP_UP_CREDIT_ALERT_THRESHOLDS,
        ),
    }));
  };

  const handleCreditAlertThresholdSelect = (group, value) => {
    if (value === CREATE_CREDIT_ALERT_THRESHOLD_VALUE) {
      setCreditAlertThresholdOpenGroup(null);
      setDraftCreditAlertThresholdAmount('');
      setCreditAlertThresholdTargetGroup(group);
      window.setTimeout(() => setCreateCreditAlertThresholdOpen(true), 0);
    }
  };

  const handleInfieldLanguageSelectChange = (value) => {
    if (value === CREATE_INFIELD_LANGUAGE_VALUE) {
      setDraftInfieldLanguageName('');
      setCreateInfieldLanguageOpen(true);
      return;
    }
    setInfieldLanguageSelectValue(normalizeInfieldLanguageName(value));
  };

  const handleCreateInfieldLanguage = () => {
    const nextLanguage = normalizeInfieldLanguageName(draftInfieldLanguageName);
    if (nextLanguage === 'NA') return;
    setDraft((current) => ({
      ...current,
      infield_languages: normalizeInfieldLanguageOptions([
        ...(current?.infield_languages || []),
        nextLanguage,
      ]),
    }));
    setInfieldLanguageSelectValue(nextLanguage);
    setDraftInfieldLanguageName('');
    setCreateInfieldLanguageOpen(false);
  };

  const handleDeleteInfieldLanguage = () => {
    const target = normalizeInfieldLanguageName(deleteInfieldLanguageTarget);
    if (!canDeleteInfieldLanguage(target)) {
      setDeleteInfieldLanguageTarget('');
      return;
    }
    setDraft((current) => ({
      ...current,
      infield_languages: normalizeInfieldLanguageOptions(
        (current?.infield_languages || []).filter(
          (item) => String(item || '').trim().toLowerCase() !== target.toLowerCase()
        )
      ),
    }));
    setDeleteInfieldLanguageTarget('');
  };

  const handleAgentOverrideChange = (agentId, field, value) => {
    setDraft((current) => {
      const overrides = { ...(current.agent_schedule_overrides || {}) };
      if (!overrides[agentId]) {
        overrides[agentId] = DEFAULT_AGENT_SCHEDULE_OVERRIDE;
      }
      overrides[agentId] = { ...overrides[agentId], [field]: value };
      return { ...current, agent_schedule_overrides: overrides };
    });
  };

  const handleSave = async () => {
    if (!selectedUserId || !draft) return;

    try {
      setSaving(true);
      const allowedVoices = allVoices.length
        ? (draft.allowed_voices || []).filter((voiceName) => activeVoiceNames.has(voiceName))
        : (draft.allowed_voices || []);
      const allowedDidNumbers = allDidNumbers.length
        ? (draft.allowed_did_numbers || []).filter((didNumberId) => activeDidNumberIds.has(didNumberId))
        : (draft.allowed_did_numbers || []);
      const retryDidNumbers = allDidNumbers.length
        ? (draft.retry_did_numbers || []).filter((didNumberId) => activeDidNumberIds.has(didNumberId))
        : (draft.retry_did_numbers || []);
      const payload = {
        name: draft.name || selectedUser?.name || 'User',
        email: draft.email || null,
        phone: draft.phone || null,
        role: draft.role || 'member',
        agent_create_feature: !!draft.agent_create_feature,
        external_prompt_feature: !!draft.external_prompt_feature,
        analytic: !!draft.analytic,
        workflow_feature: !!draft.workflow_feature,
        crm_feature: !!draft.crm_feature,
        call_analytics_feature: !!draft.call_analytics_feature,
        chatbot_feature: !!draft.chatbot_feature,
        whatsapp_feature: !!draft.whatsapp_feature,
        inbound_calling_feature: !!draft.inbound_calling_feature,
        voice_clone_feature: !!draft.voice_clone_feature,
        products_feature: !!draft.products_feature,
        knowledge_feature: !!draft.knowledge_feature,
        add_credit_feature: !!draft.add_credit_feature,
        timeliner: !!draft.timeliner,
        demo_mode: !!draft.demo_mode,
        call_outcome: !!draft.call_outcome,
        conversation_metadata: !!draft.conversation_metadata,
        show_customer_phone: !!draft.show_customer_phone,
        background_noise_simulation: !!draft.background_noise_simulation,
        interruption_handling: !!draft.interruption_handling,
        primary_did_rotation: !!draft.primary_did_rotation,
        did_limit_rotation: !!draft.did_limit_rotation,
        hang_up_on_silence: !!draft.hang_up_on_silence,
        total_call_timeout: !!draft.total_call_timeout,
        is_skip_credit_enabled: !!draft.is_skip_credit_enabled,
        audit_and_billing: draft.audit_and_billing ?? true,
        command_center: draft.command_center ?? true,
        supervision: !!draft.supervision,
        batch_call_concurrency: Number(draft.batch_call_concurrency || 0),
        free_credit_limit: Number(draft.free_credit_limit ?? 100),
        free_credit_pulse: Number(draft.free_credit_pulse ?? 60),
        free_credit_status: draft.free_credit_status || 'started',
        paid_credit_limit: Number(draft.paid_credit_limit ?? 10000),
        paid_credit_pulse: Number(draft.paid_credit_pulse ?? 60),
        paid_credit_status: draft.paid_credit_status || 'not_started',
        credit_alert_enabled: draft.credit_alert_enabled ?? true,
        credit_alert_promotional_thresholds: normalizeCreditAlertThresholds(
          draft.credit_alert_promotional_thresholds,
          DEFAULT_PROMOTIONAL_CREDIT_ALERT_THRESHOLDS,
        ),
        credit_alert_top_up_thresholds: normalizeCreditAlertThresholds(
          draft.credit_alert_top_up_thresholds,
          DEFAULT_TOP_UP_CREDIT_ALERT_THRESHOLDS,
        ),
        credit_alert_max_email_sends_per_threshold: Math.min(
          50,
          Math.max(1, Math.round(Number(draft.credit_alert_max_email_sends_per_threshold) || 5)),
        ),
        // The backend only accepts a strict zero-padded HH:MM, so fall back to the
        // default rather than sending a half-typed or cleared value.
        credit_alert_daily_email_time_ist:
          /^([01]\d|2[0-3]):[0-5]\d$/.test(draft.credit_alert_daily_email_time_ist || '')
            ? draft.credit_alert_daily_email_time_ist
            : '10:30',
        // No standalone toggle for this anymore — the Feature Flags "Skip
        // Credit Limit" switch (is_skip_credit_enabled) is the only on/off
        // control, so mirror it straight into the stored runtime status.
        skip_credit_status: !!draft.is_skip_credit_enabled,
        skip_credit_upto: Number(draft.skip_credit_upto ?? 0),
        csv_upload_limit: Number(draft.csv_upload_limit ?? 1000),
        callback_limit_days: Math.min(365, Math.max(1, Math.round(Number(draft.callback_limit_days) || 31))),
        allowed_voices: allowedVoices,
        allowed_did_numbers: allowedDidNumbers,
        retry_did_numbers: retryDidNumbers,
        infield_languages: normalizeInfieldLanguageOptions(draft.infield_languages || []),
        hidden_agent_ids: draft.hidden_agent_ids || [],
        agent_schedule_overrides: Object.fromEntries(
          Object.entries(draft.agent_schedule_overrides || {}).map(([key, { nexgen_thinker, ...value } = {}]) => [
            key,
            {
              ...value,
              noise_cancellation: value.noise_cancellation ?? false,
              speaker_isolation: value.speaker_isolation ?? false,
              thinking_nextgen: value.thinking_nextgen ?? nexgen_thinker ?? false,
            },
          ])
        ),
      };

      const response = await axios.patch(`${apiUrl}/api/v1/admin/users/${selectedUserId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updatedUser = response.data;
      setSelectedUser(updatedUser);
      setDraft(updatedUser);
      if (updatedUser?.id === user?.id) {
        void refreshUser(undefined, { force: true }).catch(() => {});
      }
      setUsers((current) => current.map((entry) => (
        entry.id === updatedUser.id ? updatedUser : entry
      )));
      if (updatedUser.id === user?.id) {
        await refreshUser();
      }
      toast.success('User settings updated.');
    } catch (error) {
      console.error('Failed to update user', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(e => e.msg || JSON.stringify(e)).join(', ')
          : 'Failed to update user settings.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <main className="flex-1 flex flex-col min-w-0 p-6 lg:p-8">
        <div className="mx-auto space-y-6 max-w-7xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold border rounded-full border-violet-100 bg-violet-50 text-violet-700">
                <ShieldCheck className="h-3.5 w-3.5" />
                Admin Controls
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">User Access Manager</h1>
              <p className="mt-1 text-slate-500">Control account-level features, limits, and scheduling defaults from one place.</p>
            </div>

            <Button
              variant="outline"
              onClick={fetchUsers}
              disabled={loadingUsers}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              {loadingUsers ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh Users
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
            <div className="bg-white border shadow-sm rounded-2xl border-slate-200">
              <div className="p-4 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search users..."
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="max-h-[72vh] overflow-y-auto">
                {loadingUsers ? (
                  <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading users...
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="px-4 py-10 text-sm text-center text-slate-500">
                    No users match the current search.
                  </div>
                ) : (
                  filteredUsers.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setSelectedUserId(entry.id)}
                      className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-4 text-left transition-colors last:border-b-0 ${selectedUserId === entry.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                        }`}
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-violet-100 text-violet-700">
                        <UserRound className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate text-slate-900">{entry.name || 'Unnamed User'}</div>
                        <div className="text-sm truncate text-slate-500">{entry.email || entry.phone || 'No contact info'}</div>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">{entry.role}</span>
                          <span className={`rounded-full px-2 py-0.5 font-medium ${entry.agent_create_feature ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                            Agent {entry.agent_create_feature ? 'On' : 'Off'}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 font-medium ${entry.analytic ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                            Analytics {entry.analytic ? 'On' : 'Off'}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white border shadow-sm rounded-2xl border-slate-200">
              {loadingUser || !draft ? (
                <div className="flex min-h-[520px] items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {selectedUserId ? 'Loading user settings...' : 'Select a user to manage access.'}
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div className="flex flex-col gap-4 pb-6 border-b border-slate-100 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">{draft.name || 'Unnamed User'}</h2>
                      <p className="mt-1 text-sm text-slate-500">{draft.email || draft.phone || 'No contact info available'}</p>
                      <div className="flex flex-wrap gap-2 mt-3 text-xs">
                        <span className="px-3 py-1 font-medium rounded-full bg-slate-100 text-slate-600">User ID: {draft.id}</span>
                        <span className="px-3 py-1 font-medium rounded-full bg-slate-100 text-slate-600">Created: {draft.created_at || '—'}</span>
                      </div>
                    </div>

                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={draft.name || ''} onChange={(event) => handleFieldChange('name', event.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={draft.email || ''} onChange={(event) => handleFieldChange('email', event.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select value={draft.role || 'member'} onValueChange={(value) => handleFieldChange('role', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((role) => (
                            <SelectItem key={role} value={role}>{role}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex flex-wrap w-fit rounded-2xl border border-slate-200/60 bg-slate-100/80 p-1.5 shadow-inner">
                    {ADMIN_SETTINGS_TABS.map((tab) => (
                      <button
                        key={tab.value}
                        type="button"
                        onClick={() => setActiveSettingsTab(tab.value)}
                        className={`rounded-[14px] px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${activeSettingsTab === tab.value
                          ? 'border border-slate-100 bg-white text-violet-700 shadow-md'
                          : 'text-slate-400 hover:text-slate-600'
                          }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {activeSettingsTab === 'feature-flags' && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Feature Flags</h3>
                      <p className="text-sm text-slate-500">If a feature is off, the frontend disables it and the backend blocks the protected action.</p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                      {BOOLEAN_FIELDS.filter(field => field.key !== 'external_prompt_feature' || !!draft.agent_create_feature).map((field) => (
                        <div key={field.key} className={`p-4 border rounded-xl border-slate-200 ${field.key === 'external_prompt_feature' ? 'ml-5 bg-slate-50' : ''}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="font-medium text-slate-900">{field.label}</div>
                              <p className="mt-1 text-sm text-slate-500">{field.description}</p>
                              {field.key === 'call_analytics_feature' && draft.call_analytics_feature && (
                                <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setCallAnalyticsLimitsOpen(true)}>
                                  Call analytics limits
                                </Button>
                              )}
                            </div>
                            <Switch
                              checked={!!draft[field.key]}
                              onCheckedChange={(checked) => handleBooleanChange(field.key, checked)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 border rounded-xl border-slate-200">
                      <div className="space-y-3">
                        <div>
                          <div className="font-medium text-slate-900">Infield Languages</div>
                          <p className="mt-1 text-sm text-slate-500">
                            Manage the language options shown in the infield normalization dropdown. Hindi and English stay available by default.
                          </p>
                        </div>
                        <Select value={infieldLanguageSelectValue} onValueChange={handleInfieldLanguageSelectChange}>
                          <SelectTrigger className="w-full max-w-md h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {infieldLanguageOptions.map((language) => (
                              <SelectItem
                                key={language}
                                value={language}
                                action={canDeleteInfieldLanguage(language) ? (
                                  <button
                                    type="button"
                                    className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                    aria-label={`Delete ${language}`}
                                    onPointerDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setDeleteInfieldLanguageTarget(language);
                                    }}
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                ) : null}
                              >
                                {language}
                              </SelectItem>
                            ))}
                            <SelectItem value={CREATE_INFIELD_LANGUAGE_VALUE}>Create New Infield Language</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  )}

                  {activeSettingsTab === 'limits' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Limits And Scheduling</h3>
                      <p className="text-sm text-slate-500">These values flow into schedule defaults and backend validation.</p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Batch Call Concurrency</Label>
                        <Input
                          type="number"
                          min="0"
                          value={draft.batch_call_concurrency ?? 0}
                          onChange={(event) => handleFieldChange('batch_call_concurrency', event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CSV Upload Limit</Label>
                        <Input
                          type="number"
                          min="0"
                          value={draft.csv_upload_limit ?? 1000}
                          onChange={(event) => handleFieldChange('csv_upload_limit', event.target.value)}
                        />
                        <p className="text-xs text-slate-500">
                          Max contacts per CSV upload / single campaign for this user.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Callback Limit (days)</Label>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          value={draft.callback_limit_days ?? 31}
                          onChange={(event) => handleFieldChange('callback_limit_days', event.target.value)}
                        />
                        <p className="text-xs text-slate-500">
                          Max future callback date (today + days). If user asks beyond this, PCA caps to max.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 max-w-xs" style={{ marginBottom: '32px' }}>
                      <Label>Skip Credit Upto</Label>
                      <Input
                        type="number"
                        min="0"
                        disabled={!draft.is_skip_credit_enabled}
                        value={draft.skip_credit_upto ?? 0}
                        onChange={(event) => handleFieldChange('skip_credit_upto', event.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="p-4 border rounded-xl border-slate-200 space-y-3">
                          <h4 className="text-sm font-semibold text-slate-900">Free Credit Limit</h4>
                          <div className="space-y-2">
                            <Label>Total Credit</Label>
                            <Input
                              type="number"
                              min="0"
                              value={draft.free_credit_limit ?? 100}
                              onChange={(event) => handleFieldChange('free_credit_limit', event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Pulse</Label>
                            <Input
                              type="number"
                              min="0"
                              value={draft.free_credit_pulse ?? 60}
                              onChange={(event) => handleFieldChange('free_credit_pulse', event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Status</Label>
                            <Select
                              value={draft.free_credit_status || 'started'}
                              onValueChange={(value) => handleFieldChange('free_credit_status', value)}
                            >
                              <SelectTrigger className="h-9 w-full"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CREDIT_STATUS_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="p-4 border rounded-xl border-slate-200 space-y-3">
                          <h4 className="text-sm font-semibold text-slate-900">Paid Credit Limit</h4>
                          <div className="space-y-2">
                            <Label>Total Credit</Label>
                            <Input
                              type="number"
                              min="0"
                              value={draft.paid_credit_limit ?? 10000}
                              onChange={(event) => handleFieldChange('paid_credit_limit', event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Pulse</Label>
                            <Input
                              type="number"
                              min="0"
                              value={draft.paid_credit_pulse ?? 60}
                              onChange={(event) => handleFieldChange('paid_credit_pulse', event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Status</Label>
                            <Select
                              value={draft.paid_credit_status || 'not_started'}
                              onValueChange={(value) => handleFieldChange('paid_credit_status', value)}
                            >
                              <SelectTrigger className="h-9 w-full"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CREDIT_STATUS_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                    </div>
                  </div>
                  )}

                  {activeSettingsTab === 'credit-alert' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Credit Alert</h3>
                      <p className="text-sm text-slate-500">
                        Configure whether low-balance credit alerts are active for this account.
                      </p>
                    </div>

                    <div className="p-4 border rounded-xl border-slate-200">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-medium text-slate-900">Enable Credit Alert</div>
                          <p className="mt-1 text-sm text-slate-500">
                            Enabled by default. If disabled, this account should not receive low-balance credit alert emails.
                          </p>
                        </div>
                        <Switch
                          checked={draft.credit_alert_enabled ?? true}
                          onCheckedChange={(checked) => handleBooleanChange('credit_alert_enabled', checked)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2 max-w-sm">
                      <Label htmlFor="credit-alert-max-email-sends">Alert Emails per Threshold</Label>
                      <Input
                        id="credit-alert-max-email-sends"
                        type="number"
                        min={1}
                        max={50}
                        value={draft.credit_alert_max_email_sends_per_threshold ?? 5}
                        onChange={(event) => {
                          const raw = event.target.value;
                          handleFieldChange(
                            'credit_alert_max_email_sends_per_threshold',
                            raw === '' ? '' : Number(raw),
                          );
                        }}
                      />
                      <p className="text-sm text-slate-500">
                        How many low-balance alert emails are sent for each threshold before it
                        goes quiet. The first goes out as soon as a call takes the balance below
                        the threshold; the rest follow once a day. Defaults to 5. Resets for a
                        threshold once the balance is topped back above it.
                      </p>
                    </div>

                    <div className="space-y-2 max-w-sm">
                      <Label htmlFor="credit-alert-daily-time">Daily Alert Email Time (IST)</Label>
                      <Input
                        id="credit-alert-daily-time"
                        type="time"
                        value={draft.credit_alert_daily_email_time_ist || '10:30'}
                        onChange={(event) =>
                          handleFieldChange('credit_alert_daily_email_time_ist', event.target.value)
                        }
                      />
                      <p className="text-sm text-slate-500">
                        IST time the follow-up alert emails go out each day, for as long as the
                        balance stays below a threshold. Defaults to 10:30 AM.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">Promotional Credits</h4>
                        <p className="text-sm text-slate-500">
                          Defaults to 90, 70, and 50. Values stay sorted in descending order automatically.
                        </p>
                      </div>

                      <div className="space-y-2 max-w-sm">
                        <Label>Threshold Amount</Label>
                        <Select
                          value={String(normalizeCreditAlertThresholds(draft.credit_alert_promotional_thresholds, DEFAULT_PROMOTIONAL_CREDIT_ALERT_THRESHOLDS)[0] || '')}
                          onValueChange={(value) => handleCreditAlertThresholdSelect('promotional', value)}
                          open={creditAlertThresholdOpenGroup === 'promotional'}
                          onOpenChange={(open) => setCreditAlertThresholdOpenGroup(open ? 'promotional' : null)}
                        >
                          <SelectTrigger className="w-full h-10">
                            <SelectValue placeholder="Manage promotional thresholds" />
                          </SelectTrigger>
                          <SelectContent>
                            {normalizeCreditAlertThresholds(draft.credit_alert_promotional_thresholds, DEFAULT_PROMOTIONAL_CREDIT_ALERT_THRESHOLDS).map((threshold) => (
                              <SelectItem
                                key={`promo-${threshold}`}
                                value={String(threshold)}
                                action={
                                  <button
                                    type="button"
                                    className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                    aria-label={`Delete ${threshold}`}
                                    onPointerDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleRemoveCreditAlertThreshold('promotional', threshold);
                                    }}
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                }
                              >
                                {threshold}
                              </SelectItem>
                            ))}
                            <SelectItem value={CREATE_CREDIT_ALERT_THRESHOLD_VALUE}>Create New Threshold</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">Top-up Credits</h4>
                        <p className="text-sm text-slate-500">
                          Defaults to 10000, 5000, 2000, and 500. Values stay sorted in descending order automatically.
                        </p>
                      </div>

                      <div className="space-y-2 max-w-sm">
                        <Label>Threshold Amount</Label>
                        <Select
                          value={String(normalizeCreditAlertThresholds(draft.credit_alert_top_up_thresholds, DEFAULT_TOP_UP_CREDIT_ALERT_THRESHOLDS)[0] || '')}
                          onValueChange={(value) => handleCreditAlertThresholdSelect('top_up', value)}
                          open={creditAlertThresholdOpenGroup === 'top_up'}
                          onOpenChange={(open) => setCreditAlertThresholdOpenGroup(open ? 'top_up' : null)}
                        >
                          <SelectTrigger className="w-full h-10">
                            <SelectValue placeholder="Manage top-up thresholds" />
                          </SelectTrigger>
                          <SelectContent>
                            {normalizeCreditAlertThresholds(draft.credit_alert_top_up_thresholds, DEFAULT_TOP_UP_CREDIT_ALERT_THRESHOLDS).map((threshold) => (
                              <SelectItem
                                key={`topup-${threshold}`}
                                value={String(threshold)}
                                action={
                                  <button
                                    type="button"
                                    className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                    aria-label={`Delete ${threshold}`}
                                    onPointerDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleRemoveCreditAlertThreshold('top_up', threshold);
                                    }}
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                }
                              >
                                {threshold}
                              </SelectItem>
                            ))}
                            <SelectItem value={CREATE_CREDIT_ALERT_THRESHOLD_VALUE}>Create New Threshold</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                  </div>
                  )}

                  {activeSettingsTab === 'agent-scheduling' && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Agent Scheduling</h3>
                      <p className="text-sm text-slate-500">Set schedule windows per agent.</p>
                    </div>

                    <div className="relative max-w-md">
                      <Search className="absolute w-4 h-4 text-slate-400 left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        value={agentScheduleSearchQuery}
                        onChange={(event) => setAgentScheduleSearchQuery(event.target.value)}
                        placeholder="Search agent or workflow..."
                        className="pl-10"
                      />
                    </div>

                    <div className="space-y-4">
                      {userAgents.length === 0 && userWorkflows.length === 0 ? (
                        <div className="text-sm text-slate-500">No agents found for this user.</div>
                      ) : filteredAgentScheduleRows.length === 0 ? (
                        <div className="text-sm text-slate-500">No agents match your search.</div>
                      ) : (
                        filteredAgentScheduleRows.map((row) => {
                          const override = draft.agent_schedule_overrides?.[row.overrideKey] || {};
                          const isHidden = (draft.hidden_agent_ids || []).includes(row.visibilityKey);
                          return (
                            <div key={row.key} className="p-4 space-y-3 border rounded-xl border-slate-200 bg-slate-50/50">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                                  {row.label}
                                  {row.isWorkflow && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-violet-100 text-violet-700">
                                      Workflow
                                    </span>
                                  )}
                                  {isHidden && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-slate-200 text-slate-600">
                                      Hidden
                                    </span>
                                  )}
                                </h4>
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs text-slate-500">Hide from user</Label>
                                  <Switch
                                    checked={isHidden}
                                    onCheckedChange={(checked) => handleHiddenAgentToggle(row.visibilityKey, checked)}
                                  />
                                </div>
                              </div>
                              <div className="grid items-end grid-cols-1 gap-4 md:grid-cols-4 lg:grid-cols-12">
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Time Start</Label>
                                  <Input
                                    value={override.schedule_time_start || DEFAULT_AGENT_SCHEDULE_OVERRIDE.schedule_time_start}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'schedule_time_start', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Time End</Label>
                                  <Input
                                    value={override.schedule_time_end || DEFAULT_AGENT_SCHEDULE_OVERRIDE.schedule_time_end}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'schedule_time_end', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-2 lg:col-span-6">
                                  <Label className="text-xs">Timezone</Label>
                                  <Input
                                    value={override.schedule_timezone || DEFAULT_AGENT_SCHEDULE_OVERRIDE.schedule_timezone}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'schedule_timezone', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Callback Time Start</Label>
                                  <Input
                                    value={override.callback_time_start || DEFAULT_AGENT_SCHEDULE_OVERRIDE.callback_time_start}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'callback_time_start', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Callback Time End</Label>
                                  <Input
                                    value={override.callback_time_end || DEFAULT_AGENT_SCHEDULE_OVERRIDE.callback_time_end}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'callback_time_end', e.target.value)}
                                  />
                                </div>
                                <div className="hidden md:block md:col-span-2 lg:col-span-6" aria-hidden="true" />
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Call Retry Enabled</Label>
                                  <div className="flex h-10 items-center">
                                    <Switch
                                      checked={override.call_retry_enabled ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.call_retry_enabled}
                                      onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'call_retry_enabled', checked)}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Short Call Retry Enabled</Label>
                                  <div className="flex h-10 items-center">
                                    <Switch
                                      checked={override.short_call_retry_enabled ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.short_call_retry_enabled}
                                      onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'short_call_retry_enabled', checked)}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">LLM Skip Enabled</Label>
                                  <div className="flex h-10 items-center">
                                    <Switch
                                      checked={override.llm_skip_enabled ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.llm_skip_enabled}
                                      onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'llm_skip_enabled', checked)}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Noise Cancellation</Label>
                                  <div className="flex h-10 items-center">
                                    <Switch
                                      checked={override.noise_cancellation ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.noise_cancellation}
                                      onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'noise_cancellation', checked)}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Speaker Isolation</Label>
                                  <div className="flex h-10 items-center">
                                    <Switch
                                      checked={override.speaker_isolation ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.speaker_isolation}
                                      onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'speaker_isolation', checked)}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Nextgen Thinker</Label>
                                  {/* Overrides saved before core kept this key used nexgen_thinker. */}
                                  <div className="flex h-10 items-center">
                                    <Switch
                                      checked={override.thinking_nextgen ?? override.nexgen_thinker ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.thinking_nextgen}
                                      onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'thinking_nextgen', checked)}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">LLM Skip Seconds</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={override.llm_skip_seconds ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.llm_skip_seconds}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'llm_skip_seconds', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Call Retry Attempts</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={override.call_retry_attempts ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.call_retry_attempts}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'call_retry_attempts', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Call Retry Delay Minutes</Label>
                                  {(() => {
                                    const attemptsCount = Math.max(0, parseInt(override.call_retry_attempts ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.call_retry_attempts, 10) || 0);
                                    const delays = getPerAttemptDelays(
                                      override.call_retry_delay_minutes ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.call_retry_delay_minutes,
                                      attemptsCount,
                                    );
                                    if (attemptsCount === 0) {
                                      return <p className="text-[10px] text-slate-400">Set Call Retry Attempts ≥ 1 to configure delays.</p>;
                                    }
                                    return (
                                      <div className="flex flex-wrap gap-2">
                                        {delays.map((val, idx) => (
                                          <Input
                                            key={idx}
                                            type="number"
                                            min="0"
                                            className="w-20"
                                            title={`Delay before retry #${idx + 1} (minutes)`}
                                            value={val}
                                            onChange={(e) => {
                                              const next = [...delays];
                                              next[idx] = e.target.value;
                                              handleAgentOverrideChange(row.overrideKey, 'call_retry_delay_minutes', next.join(','));
                                            }}
                                          />
                                        ))}
                                      </div>
                                    );
                                  })()}
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Short Call Retry Seconds</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={override.short_call_retry_seconds ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.short_call_retry_seconds}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'short_call_retry_seconds', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Auto Disconnect Seconds</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={override.auto_disconnect_timeout_seconds ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.auto_disconnect_timeout_seconds}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'auto_disconnect_timeout_seconds', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">User Away Timeout Seconds</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={override.user_away_timeout_seconds ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.user_away_timeout_seconds}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'user_away_timeout_seconds', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Away Countdown Seconds</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={override.away_disconnect_countdown_seconds ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.away_disconnect_countdown_seconds}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'away_disconnect_countdown_seconds', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-2 lg:col-span-6">
                                  <Label className="text-xs">Away First Message</Label>
                                  <Input
                                    value={override.away_first_message ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.away_first_message}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'away_first_message', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-2 lg:col-span-6">
                                  <Label className="text-xs">Away Disconnect Message</Label>
                                  <Input
                                    value={override.away_disconnect_message ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.away_disconnect_message}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'away_disconnect_message', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1.5 md:col-span-2 lg:col-span-6">
                                  <Label className="text-xs">Auto Disconnect Message</Label>
                                  <Input
                                    value={override.auto_disconnect_message ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.auto_disconnect_message}
                                    onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'auto_disconnect_message', e.target.value)}
                                  />
                                </div>

                                {/* ── Call Transfer (Human Handoff) ─────────────── */}
                                <div className="md:col-span-2 lg:col-span-6 pt-2 mt-1 border-t border-slate-200">
                                  <h4 className="text-sm font-semibold text-slate-800">Call Transfer (Human Handoff)</h4>
                                  <p className="text-[11px] text-slate-400">Blind-transfer the caller to a number via SIP. All off by default.</p>
                                </div>
                                <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                  <Label className="text-xs">Transfer Enabled</Label>
                                  <div className="flex h-10 items-center">
                                    <Switch
                                      checked={override.transfer_enabled ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.transfer_enabled}
                                      onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'transfer_enabled', checked)}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1.5 md:col-span-2 lg:col-span-6">
                                  <Label className="text-xs">Transfer Destinations (one <span className="font-mono">label: number</span> per line)</Label>
                                  <TransferDestinationsEditor
                                    value={override.transfer_destinations || {}}
                                    onChange={(map) => handleAgentOverrideChange(row.overrideKey, 'transfer_destinations', map)}
                                  />
                                </div>
                                {(() => {
                                  const transferLabels = Object.keys(override.transfer_destinations || {});
                                  const labelSelect = (field) => {
                                    const current = override[field] || '';
                                    const options = current && !transferLabels.includes(current)
                                      ? [current, ...transferLabels]
                                      : transferLabels;
                                    return (
                                      <Select value={current || undefined} onValueChange={(v) => handleAgentOverrideChange(row.overrideKey, field, v)}>
                                        <SelectTrigger><SelectValue placeholder={transferLabels.length ? 'Select label' : 'Add a destination first'} /></SelectTrigger>
                                        <SelectContent>
                                          {options.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
                                        </SelectContent>
                                      </Select>
                                    );
                                  };
                                  return (
                                    <>
                                      <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                        <Label className="text-xs">Transfer On Time Limit</Label>
                                        <div className="flex h-10 items-center">
                                          <Switch
                                            checked={override.transfer_on_time_limit_enabled ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.transfer_on_time_limit_enabled}
                                            onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'transfer_on_time_limit_enabled', checked)}
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                        <Label className="text-xs">Time Limit Transfer Label</Label>
                                        {labelSelect('transfer_on_time_limit_label')}
                                      </div>
                                      <div className="space-y-1.5 md:col-span-2 lg:col-span-6">
                                        <Label className="text-xs">Time Limit Transfer Message</Label>
                                        <Input
                                          value={override.transfer_on_time_limit_message ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.transfer_on_time_limit_message}
                                          onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'transfer_on_time_limit_message', e.target.value)}
                                        />
                                      </div>
                                      <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                        <Label className="text-xs">Transfer After N Questions</Label>
                                        <div className="flex h-10 items-center">
                                          <Switch
                                            checked={override.transfer_after_questions_enabled ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.transfer_after_questions_enabled}
                                            onCheckedChange={(checked) => handleAgentOverrideChange(row.overrideKey, 'transfer_after_questions_enabled', checked)}
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                        <Label className="text-xs">Questions Threshold</Label>
                                        <Input
                                          type="number"
                                          min="0"
                                          value={override.transfer_after_questions_count ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.transfer_after_questions_count}
                                          onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'transfer_after_questions_count', e.target.value)}
                                        />
                                      </div>
                                      <div className="space-y-1.5 md:col-span-1 lg:col-span-3">
                                        <Label className="text-xs">Questions Transfer Label</Label>
                                        {labelSelect('transfer_after_questions_label')}
                                      </div>
                                      <div className="space-y-1.5 md:col-span-2 lg:col-span-6">
                                        <Label className="text-xs">Questions Transfer Message</Label>
                                        <Input
                                          value={override.transfer_after_questions_message ?? DEFAULT_AGENT_SCHEDULE_OVERRIDE.transfer_after_questions_message}
                                          onChange={(e) => handleAgentOverrideChange(row.overrideKey, 'transfer_after_questions_message', e.target.value)}
                                        />
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  )}

                  {activeSettingsTab === 'voice-access' && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Voice Access</h3>
                      <p className="text-sm text-slate-500">Select which active voices this user is allowed to use. If none are selected, no voices are available.</p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                      {allVoices.map((voice) => (
                        <div key={voice.id} className="p-4 border rounded-xl border-slate-200">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="font-medium text-slate-900">{voice.local_voice_name}</div>
                              <p className="mt-1 text-sm text-slate-500">{voice.provider || 'No provider'} • {voice.gender || 'Unknown'}</p>
                            </div>
                            <Switch
                              checked={(draft.allowed_voices || []).includes(voice.local_voice_name)}
                              onCheckedChange={(checked) => handleVoiceToggle(voice.local_voice_name, checked)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}

                  {activeSettingsTab === 'org-voices' && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Organisation Voices</h3>
                      <p className="text-sm text-slate-500">
                        Which catalogue voices this user&apos;s organisation may use. Its owner then decides
                        which member gets which. Voices the organisation cloned itself are always available
                        to it and are not listed here.
                      </p>
                    </div>

                    {!orgForUser ? (
                      <div className="p-4 text-sm border rounded-xl border-slate-200 text-slate-500">
                        This user does not own an organisation yet.
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-3 p-4 border rounded-xl border-slate-200">
                          <div className="flex-1 min-w-[12rem]">
                            <div className="font-medium text-slate-900">{orgForUser.name}</div>
                            <p className="mt-1 text-sm text-slate-500">
                              {orgVoicesDraft === null
                                ? 'Unrestricted — every catalogue voice is available.'
                                : `Restricted to ${orgVoicesDraft.length} voice(s).`}
                            </p>
                          </div>
                          {orgVoicesDraft === null ? (
                            <button
                              type="button"
                              disabled={orgVoicesSaving}
                              onClick={() => setOrgVoicesDraft([])}
                              className="px-3 py-2 text-sm font-medium border rounded-lg border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                            >
                              Restrict to a selection
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={orgVoicesSaving}
                              onClick={() => saveOrgVoices(orgVoicesDraft)}
                              className="px-3 py-2 text-sm font-semibold text-white rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                            >
                              {orgVoicesSaving ? 'Saving…' : 'Save organisation voices'}
                            </button>
                          )}
                        </div>

                        {orgVoicesDraft !== null && (
                          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                            {allVoices.map((voice) => (
                              <div key={voice.id} className="p-4 border rounded-xl border-slate-200">
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <div className="font-medium text-slate-900">{voice.local_voice_name}</div>
                                    <p className="mt-1 text-sm text-slate-500">{voice.provider || 'No provider'} • {voice.gender || 'Unknown'}</p>
                                  </div>
                                  <Switch
                                    checked={orgVoicesDraft.includes(voice.local_voice_name)}
                                    onCheckedChange={(checked) =>
                                      setOrgVoicesDraft((current) =>
                                        checked
                                          ? [...(current || []), voice.local_voice_name]
                                          : (current || []).filter((name) => name !== voice.local_voice_name),
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  )}

                  {activeSettingsTab === 'did-number-access' && (
                  <div className="space-y-4">
                    {allDidNumbers.length === 0 ? (
                      <>
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">DID Number Access</h3>
                          <p className="text-sm text-slate-500">Select which active DID numbers this user is allowed to use. If none are selected, no primary DID numbers are available.</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                          No active DID numbers are available right now.
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <h4 className="text-sm font-semibold text-slate-900">Retry DID Numbers</h4>
                              <p className="mt-1 text-sm text-slate-500">Select active DID numbers for retry attempts.</p>
                            </div>
                            <div className="w-full lg:w-[420px]">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-violet-200 bg-white px-3 py-2 text-left text-sm shadow-sm transition hover:border-violet-300"
                                  >
                                    <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                                      {selectedRetryDidNumbers.length ? (
                                        selectedRetryDidNumbers.map((didNumber) => (
                                          <span
                                            key={didNumber.id}
                                            className="inline-flex max-w-full items-center gap-1 rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-slate-700"
                                          >
                                            <span className="truncate">{didNumber.number || 'Unnamed DID Number'}</span>
                                          </span>
                                        ))
                                      ) : (
                                        <span className="text-slate-400">Select retry DID numbers</span>
                                      )}
                                    </div>
                                    <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-[420px] max-w-[calc(100vw-3rem)] p-2">
                                  <div className="max-h-72 space-y-1 overflow-y-auto">
                                    {allDidNumbers.map((didNumber) => {
                                      const isChecked = (draft.retry_did_numbers || []).includes(didNumber.id);
                                      return (
                                        <div
                                          key={didNumber.id}
                                          role="button"
                                          tabIndex={0}
                                          className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-violet-50"
                                          onClick={() => handleRetryDidNumberToggle(didNumber.id, !isChecked)}
                                          onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                              event.preventDefault();
                                              handleRetryDidNumberToggle(didNumber.id, !isChecked);
                                            }
                                          }}
                                        >
                                          <span
                                            className="mt-1 flex shrink-0 items-center justify-center"
                                            style={{
                                              width: '16px',
                                              height: '16px',
                                              borderRadius: '4px',
                                              border: isChecked ? '2px solid #7c3aed' : '2px solid #000000',
                                              backgroundColor: isChecked ? '#7c3aed' : '#ffffff',
                                            }}
                                          >
                                            {isChecked && <Check className="h-3 w-3" style={{ color: '#ffffff' }} />}
                                          </span>
                                          <div className="min-w-0 flex-1">
                                            <span className="font-medium break-all text-slate-900">{didNumber.number || 'Unnamed DID Number'}</span>
                                            <p className="mt-1 text-xs break-all text-slate-500">
                                              {didNumber.provider || 'Unknown'} - {didNumber.sip_trunk_id || 'No trunk'}
                                            </p>
                                            <p className="mt-0.5 text-xs break-all text-slate-500">
                                              LiveKit Agent: {didNumber.livekit_agent_name || 'Not set'}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-base font-semibold text-slate-900">DID Number Access</h3>
                          <p className="text-sm text-slate-500">Select which active DID numbers this user is allowed to use. If none are selected, no primary DID numbers are available.</p>
                        </div>

                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                          {allDidNumbers.map((didNumber) => (
                            <div key={didNumber.id} className="p-4 border rounded-xl border-slate-200">
                              <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <div className="font-medium break-all text-slate-900">{didNumber.number || 'Unnamed DID Number'}</div>
                                  </div>
                                  <p className="mt-1 text-sm text-slate-500">
                                    Provider: {didNumber.provider || 'Unknown'}
                                  </p>
                                  <p className="mt-1 text-sm break-all text-slate-500">
                                    SIP Trunk ID: {didNumber.sip_trunk_id || 'Not set'}
                                  </p>
                                  <p className="mt-1 text-sm break-all text-slate-500">
                                    LiveKit Agent: {didNumber.livekit_agent_name || 'Not set'}
                                  </p>
                                </div>
                                <Switch
                                  checked={(draft.allowed_did_numbers || []).includes(didNumber.id)}
                                  onCheckedChange={(checked) => handleDidNumberToggle(didNumber.id, checked)}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  )}


                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Dialog open={createInfieldLanguageOpen} onOpenChange={setCreateInfieldLanguageOpen}>
        <DialogContent className="sm:max-w-[380px] rounded-2xl border-slate-100 bg-white shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              Create Infield Language
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-600">Language name</label>
            <Input
              value={draftInfieldLanguageName}
              onChange={(event) => setDraftInfieldLanguageName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleCreateInfieldLanguage();
                }
              }}
              placeholder="Language name"
              className="h-9"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDraftInfieldLanguageName('');
                setCreateInfieldLanguageOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCreateInfieldLanguage}
              disabled={normalizeInfieldLanguageName(draftInfieldLanguageName) === 'NA'}
              className="bg-violet-600 text-white hover:bg-violet-700"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteInfieldLanguageTarget)} onOpenChange={(open) => !open && setDeleteInfieldLanguageTarget('')}>
        <DialogContent className="sm:max-w-[360px] rounded-2xl border-slate-100 bg-white shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              Delete Infield Language
            </DialogTitle>
            <DialogDescription>
              Delete "{deleteInfieldLanguageTarget}" from saved infield languages?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteInfieldLanguageTarget('')}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDeleteInfieldLanguage}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createCreditAlertThresholdOpen} onOpenChange={setCreateCreditAlertThresholdOpen}>
        <DialogContent className="sm:max-w-[380px] rounded-2xl border-slate-100 bg-white shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              Create Threshold
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-600">Threshold amount</label>
            <Input
              type="number"
              min="1"
              value={draftCreditAlertThresholdAmount}
              onChange={(event) => setDraftCreditAlertThresholdAmount(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddCreditAlertThreshold();
                }
              }}
              placeholder="Threshold amount"
              className="h-9"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDraftCreditAlertThresholdAmount('');
                setCreateCreditAlertThresholdOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddCreditAlertThreshold}
              disabled={!draftCreditAlertThresholdAmount || Number(draftCreditAlertThresholdAmount) <= 0}
              className="bg-violet-600 text-white hover:bg-violet-700"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {callAnalyticsLimitsOpen && selectedUserId && (
        <CallAnalyticsLimitsDialog
          userId={selectedUserId}
          userName={selectedUser?.name || selectedUser?.email || 'this user'}
          onClose={() => setCallAnalyticsLimitsOpen(false)}
        />
      )}
    </div>
  );
}
